import * as signalR from '@microsoft/signalr';
import { FailedToNegotiateWithServerError } from '@microsoft/signalr/dist/esm/Errors';
import { inject, injectable } from 'inversify';
import { action, makeObservable, reaction } from 'mobx';
import { IAuthStore } from '../modules/auth/auth.store';

interface IBaseHubService {
  initializeAsync(): Promise<void>;
}

@injectable()
export abstract class BaseHubService implements IBaseHubService {
  protected connection: signalR.HubConnection;

  constructor(
    @inject(IAuthStore.$) private readonly auth: IAuthStore,
    private readonly hubUrl: string
  ) {
    makeObservable(this);
    this.connection = this.buildConnection();
    this.connection.onclose(() => {
      void this.connectAsync();
    });
  }

  buildConnection(): signalR.HubConnection {
    return new signalR.HubConnectionBuilder()
      .withUrl(this.hubUrl, { accessTokenFactory: () => this.auth.token })
      .configureLogging(signalR.LogLevel.Critical)
      .withAutomaticReconnect()
      .build();
  }

  @action.bound
  public async initializeAsync(): Promise<void> {
    await this.connectAsync();
  }

  disconnectHub = async (): Promise<void> => {
    await this.connection.stop();
  };

  async connectAsync(): Promise<void> {
    if (!this.auth.isAuthenticated) {
      return;
    }
    if (this.connection.state === signalR.HubConnectionState.Connected) {
      return;
    }

    try {
      await this.connection.start();
      reaction(
        () => this.auth.token,
        () => {
          if (!this.auth.token) {
            void this.disconnectHub();
          }
        }
      );
    } catch (error) {
      if (this.stopConnecting(error)) {
        return;
      }
      setTimeout(() => {
        void this.connectAsync();
      }, 5000);
    }
  }

  stopConnecting(error: unknown): boolean {
    if (error instanceof signalR.HttpError) {
      if (error.statusCode === 401) {
        this.auth.clearToken();
        return true;
      }
      if (error.statusCode === 460) {
        return true;
      }
    } else if (error instanceof FailedToNegotiateWithServerError && error.message.includes('401')) {
      this.auth.clearToken();
      return true;
    }

    return false;
  }
}
